import React, { useEffect, useState } from 'react';
import theme from '../../theme';
import {
  getSyntheticAssetFields,
  transferSyntheticAsset,
  updateSyntheticAsset,
} from '../../services/api';

// What the operator can fill in here; account and symbol are never missing.
const EDITABLE = {
  side: { label: 'Side', options: ['BUY', 'SELL'] },
  currency: { label: 'Currency' },
  exchange: { label: 'Exchange' },
  sectype: { label: 'Sectype' },
  leverage: { label: 'Leverage', type: 'number' },
};

/**
 * A synthetic cannot trade until it is transferred to securities. Offered when a
 * promotion is refused for that reason: fill whatever is still missing, then
 * transfer and resume the promotion with the resulting contract.
 */
const SyntheticTransferModal = ({ syntheticId, onClose, onTransferred }) => {
  const [info, setInfo] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSyntheticAssetFields(syntheticId).then((data) => {
      if (data) setInfo(data);
      else setError('Could not read the synthetic asset');
    });
  }, [syntheticId]);

  const missing = (info?.missing_fields || []).filter((field) => EDITABLE[field]);
  const incomplete = missing.some((field) => !String(values[field] ?? '').trim());

  const submit = async () => {
    setBusy(true);
    setError(null);
    if (missing.length) {
      const patch = Object.fromEntries(missing.map((field) => [
        field, field === 'leverage' ? parseFloat(values[field]) : values[field].trim(),
      ]));
      if (!await updateSyntheticAsset({ id: syntheticId, ...patch })) {
        setBusy(false);
        setError('Could not save the fields');
        return;
      }
    }
    const result = await transferSyntheticAsset({ id: syntheticId, broker: info.broker });
    setBusy(false);
    if (!result) {
      setError('Transfer failed');
      return;
    }
    onClose();
    onTransferred(result.instrument);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className={`text-sm font-bold ${theme.textPrimary}`}>
            Transfer {info?.symbol || 'synthetic'} before trading
          </h3>
          <button onClick={onClose} disabled={busy} className={`text-xs ${theme.textMuted} hover:text-red-500`}>✕</button>
        </div>

        {error && <p className="mb-2 text-[11px] text-red-500">{error}</p>}

        {info && (
          <>
            <p className={`mb-3 text-xs ${theme.textMuted}`}>
              {missing.length
                ? 'This instrument is still synthetic. Fill in the missing fields and transfer it to securities.'
                : 'This instrument is complete and only waits for its transfer to securities.'}
            </p>

            <div className="space-y-2">
              {missing.map((field) => {
                const { label, options, type } = EDITABLE[field];
                const common = {
                  value: values[field] ?? '',
                  onChange: (e) => setValues({ ...values, [field]: e.target.value }),
                  className: 'w-full rounded-lg border border-gray-300 p-2 text-sm',
                };
                return (
                  <label key={field} className="block">
                    <span className={`mb-1 block text-[11px] ${theme.textMuted}`}>{label}</span>
                    {options ? (
                      <select {...common}>
                        <option value="">Select</option>
                        {options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={type || 'text'} step="0.1" min="0.1" {...common} />
                    )}
                  </label>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} disabled={busy} className={`rounded-lg px-3 py-1.5 text-xs ${theme.textMuted}`}>
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy || incomplete}
                className="rounded-lg bg-olive-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
              >
                {missing.length ? 'Save, Transfer & Trade' : 'Transfer & Trade'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SyntheticTransferModal;
